import { useEffect, useRef, useState } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import type { MaterialIntent, ProjectSpace } from "@contractor-quoting/shared-types";
import {
  clearPendingProjectFormDraft,
  savePendingProjectFormDraft,
  type PendingProjectFormDraft,
} from "../project-form-draft.js";

export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

export function useProjectAutosave(input: {
  projectId: string | null;
  ready: boolean;
  spaces: ProjectSpace[];
  materialIntent: MaterialIntent;
}): { status: AutosaveStatus; error: string } {
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [error, setError] = useState("");
  const projectId = useRef<string | null>(null);
  const latest = useRef<PendingProjectFormDraft | null>(null);
  const revision = useRef(0);
  const savedRevision = useRef(0);
  const saving = useRef(false);

  const flush = async () => {
    if (saving.current || !projectId.current || !latest.current) return;
    saving.current = true;
    setStatus("saving");
    setError("");
    while (savedRevision.current < revision.current && latest.current) {
      const targetRevision = revision.current;
      const targetProjectId = projectId.current;
      const draft = latest.current;
      try {
        await apiFetch("/v1/quote/project", {
          method: "PUT",
          body: JSON.stringify({
            spaces: draft.spaces,
            transitions: [],
            materialIntent: draft.materialIntent,
            answers: {},
          }),
        });
        savedRevision.current = targetRevision;
        if (targetRevision === revision.current) {
          clearPendingProjectFormDraft(targetProjectId);
        }
      } catch (reason) {
        setStatus("error");
        setError(reason instanceof Error ? reason.message : "Draft could not be saved");
        saving.current = false;
        return;
      }
    }
    saving.current = false;
    setStatus("saved");
  };

  useEffect(() => {
    if (!input.ready || !input.projectId) return;
    if (projectId.current !== input.projectId) {
      projectId.current = input.projectId;
      revision.current = 0;
      savedRevision.current = 0;
    }
    latest.current = { spaces: input.spaces, materialIntent: input.materialIntent };
    revision.current += 1;
    savePendingProjectFormDraft(input.projectId, latest.current);
    setStatus("idle");
    const timer = window.setTimeout(() => void flush(), 650);
    return () => window.clearTimeout(timer);
  }, [input.materialIntent, input.projectId, input.ready, input.spaces]);

  return { status, error };
}
